/**
 * BlogSearch Component
 * Search input with debounce for filtering blog posts
 */
import React, { useState, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

const BlogSearch = ({ onSearch, placeholder }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setValue(newValue);

      // Clear previous timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Set new timer for debounced search
      const timer = setTimeout(() => {
        onSearch(newValue);
      }, 300);

      setDebounceTimer(timer);
    },
    [debounceTimer, onSearch]
  );

  const handleClear = useCallback(() => {
    setValue('');
    onSearch('');
  }, [onSearch]);

  return (
    <div className="relative w-full max-w-md">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
        <Search
          className="h-5 w-5 text-neutral-400 dark:text-neutral-500"
          strokeWidth={2.5}
        />
      </div>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={
          placeholder || t('blog.searchPlaceholder', 'Buscar artículos...')
        }
        className={cn(
          'w-full rounded-xl py-3 pl-12 pr-10',
          'border border-neutral-200 bg-white',
          'dark:border-neutral-700 dark:bg-neutral-800',
          'text-neutral-900 placeholder-neutral-500',
          'dark:text-white dark:placeholder-neutral-400',
          'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
          'dark:focus:border-primary-500 dark:focus:ring-primary-500/30',
          'transition-all duration-200'
        )}
      />
      {value && (
        <button
          onClick={handleClear}
          className="absolute inset-y-0 right-0 flex items-center pr-4 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          aria-label={t('common.clear', 'Limpiar')}
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
};

export default BlogSearch;
